import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Send, Link as LinkIcon } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { useResolvedEmailTemplate } from "@/hooks/useEmailTemplate";
import { plainFooterHtml, plainFooterText } from "@/lib/aciFooter";

interface SendReportEmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reportLink: string;
  leadTitle?: string | null;
  guideName?: string | null;
  /** Provide for programme (lead) reports */
  leadId?: string;
  /** Provide for direct Coffeebeans booking reports */
  bookingId?: string;
}

export function SendReportEmailDialog({
  open,
  onOpenChange,
  reportLink,
  leadTitle,
  guideName,
  leadId,
  bookingId,
}: SendReportEmailDialogProps) {
  const [to, setTo] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const autoBodyRef = useRef("");

  const { data: lead } = useQuery({
    queryKey: ["report-email-lead-brand", leadId],
    queryFn: async () => {
      const { data } = await supabase
        .from("leads")
        .select("brand_id, client_email, client_name, client_liaison, client_id")
        .eq("id", leadId as string)
        .maybeSingle();
      return data;
    },
    enabled: open && !!leadId,
  });

  const { data: booking } = useQuery({
    queryKey: ["report-email-booking", bookingId],
    queryFn: async () => {
      const { data } = await supabase
        .from("coffeebeans_bookings")
        .select(
          "id, brand_id, guest_name, guest_email, experience_name, experience_date, experience_time, agent_code_id",
        )
        .eq("id", bookingId as string)
        .maybeSingle();
      return data as any;
    },
    enabled: open && !!bookingId,
  });

  const brandId = (lead?.brand_id ?? booking?.brand_id ?? null) as string | null;

  const { data: brand } = useQuery({
    queryKey: ["report-email-brand", brandId],
    queryFn: async () => {
      const { data } = await supabase
        .from("business_brands")
        .select("name, email, phone, website")
        .eq("id", brandId as string)
        .maybeSingle();
      return data;
    },
    enabled: open && !!brandId,
  });

  // Likely recipients: the agent/concierge who made the booking, then the client/guest contact.
  // For a session with several bookings, every booking's guest and partner is offered.
  const { data: suggestions } = useQuery({
    queryKey: [
      "report-email-recipients",
      leadId,
      bookingId,
      lead?.client_id,
      lead?.client_email,
      booking?.experience_name,
      booking?.experience_date,
      booking?.experience_time,
    ],
    queryFn: async () => {
      const out: { email: string; label: string; name: string }[] = [];
      const push = (email?: string | null, name?: string | null, role?: string) => {
        const e = (email || "").trim();
        if (!e || out.some((o) => o.email.toLowerCase() === e.toLowerCase())) return;
        const n = (name || "").trim();
        out.push({ email: e, name: n, label: n ? `${n}${role ? ` (${role})` : ""}` : e });
      };

      if (lead?.client_id) {
        const { data: partners } = await supabase
          .from("agent_codes")
          .select("agent_name, agent_company, agent_email, agent_type")
          .eq("client_id", lead.client_id);
        (partners || []).forEach((p: any) =>
          push(
            p.agent_email,
            p.agent_company || p.agent_name || "Partner",
            p.agent_type === "concierge" ? "Concierge" : "Agent",
          ),
        );

        const { data: client } = await supabase
          .from("clients")
          .select("name, email")
          .eq("id", lead.client_id)
          .maybeSingle();
        if (client) push(client.email, client.name || "Client", "Client");
      }

      if (leadId) {
        push(lead?.client_email, lead?.client_liaison || lead?.client_name || "Client contact", "Lead contact");
      }

      if (bookingId && booking) {
        // Every booking sharing this session (same experience, date and time)
        let sessionBookings: any[] = [booking];
        if (booking.experience_name && booking.experience_date) {
          let q = supabase
            .from("coffeebeans_bookings")
            .select("id, guest_name, guest_email, agent_code_id, status, deleted_at")
            .eq("experience_name", booking.experience_name)
            .eq("experience_date", booking.experience_date)
            .is("deleted_at", null);
          q = booking.experience_time
            ? q.eq("experience_time", booking.experience_time)
            : q.is("experience_time", null);
          const { data: siblings } = await q;
          const rows = (siblings || []).filter((b: any) => b.status !== "cancelled");
          if (rows.length) sessionBookings = rows;
        }

        const partnerIds = Array.from(
          new Set(sessionBookings.map((b: any) => b.agent_code_id).filter(Boolean)),
        ) as string[];
        if (partnerIds.length) {
          const { data: partners } = await supabase
            .from("agent_codes")
            .select("id, agent_name, agent_company, agent_email, agent_type")
            .in("id", partnerIds);
          (partners || []).forEach((p: any) =>
            push(
              p.agent_email,
              p.agent_company || p.agent_name || "Partner",
              p.agent_type === "concierge" ? "Concierge" : "Agent",
            ),
          );
        }

        sessionBookings.forEach((b: any) => push(b.guest_email, b.guest_name || "Guest", "Guest"));
      }

      return out;
    },
    enabled: open && (!!lead || !!booking),
  });

  // Pre-fill from the admin-editable default wording (Admin → Email Templates)
  const resolved = useResolvedEmailTemplate(
    "report_share",
    brandId,
    {
      recipient_name: recipientName.trim() || "Client",
      trip_title: leadTitle || booking?.experience_name || "the tour",
      guide_name: guideName ? `led by ${guideName}` : "",
      link: reportLink,
    },
    open,
  );

  useEffect(() => {
    if (!open || resolved.isLoading) return;
    setSubject((prev) => (prev ? prev : resolved.subject));
    // Keep the wording in sync with the recipient name until the user edits it themselves
    setMessage((prev) => (prev && prev !== autoBodyRef.current ? prev : resolved.body));
    autoBodyRef.current = resolved.body;
  }, [open, resolved.isLoading, resolved.subject, resolved.body]);

  // Always greet the person named in the Recipient name field, even after the
  // wording has been edited by hand.
  useEffect(() => {
    if (!open) return;
    const greetName = recipientName.trim() || "Client";
    setMessage((prev) => {
      if (!prev) return prev;
      const lines = prev.split("\n");
      const idx = lines.findIndex((l) => /^\s*Dear\b/i.test(l));
      if (idx === -1) return prev;
      const trailing = lines[idx].match(/[,:]\s*$/)?.[0] ?? "";
      const next = `Dear ${greetName}${trailing}`;
      if (lines[idx] === next) return prev;
      lines[idx] = next;
      return lines.join("\n");
    });
  }, [open, recipientName]);

  useEffect(() => {
    if (!open) {
      setTo("");
      setRecipientName("");
      setSubject("");
      setMessage("");
      autoBodyRef.current = "";
    }
  }, [open]);

  const sendEmailMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("send-lead-email", {
        body: {
          leadId: leadId ?? null,
          bookingId: bookingId ?? null,
          to: [to],
          subject,
          bodyText: message + "\n" + plainFooterText(brand ?? {}),
          bodyHtml: `<p>${message
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/\n/g, "<br>")}</p>${plainFooterHtml(brand ?? {})}`,
        },
      });

      if (error) throw new Error(error.message || "Failed to send email");
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      toast({
        title: "Email sent",
        description: "The activity report has been shared successfully.",
      });
      onOpenChange(false);
      // Reset form
      setTo("");
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to send email",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!to.trim()) {
      toast({
        title: "Email required",
        description: "Please enter a recipient email address.",
        variant: "destructive",
      });
      return;
    }
    sendEmailMutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg flex max-h-[90vh] flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            Share Activity Report
          </DialogTitle>
          <DialogDescription>
            Send the activity report link via email
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
          <div className="space-y-2">
            <Label htmlFor="report-to">To</Label>
            <Input
              id="report-to"
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="recipient@example.com"
              required
            />
            {(suggestions?.length ?? 0) > 0 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {suggestions!.map((s) => (
                  <Button
                    key={s.email}
                    type="button"
                    size="sm"
                    variant={to.trim().toLowerCase() === s.email.toLowerCase() ? "default" : "outline"}
                    className="h-auto py-1 text-xs"
                    onClick={() => {
                      setTo(s.email);
                      if (s.name) setRecipientName(s.name);
                    }}
                  >
                    {s.label}
                  </Button>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="report-recipient-name">Recipient name</Label>
            <Input
              id="report-recipient-name"
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              placeholder="e.g. Micaela"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="report-subject">Subject</Label>
            <Input
              id="report-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="report-message">Message</Label>
            <Textarea
              id="report-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="min-h-[150px]"
              required
            />
          </div>

          <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg text-sm">
            <LinkIcon className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="text-muted-foreground truncate">{reportLink}</span>
          </div>
          </div>

          <DialogFooter className="shrink-0 border-t pt-3 mt-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={sendEmailMutation.isPending}>
              {sendEmailMutation.isPending ? "Sending..." : "Send Email"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
